/**
 * Phone number validation and normalization utilities.
 * Accepts free-text international formats and antd-phone-input value objects.
 * Validates against E.164-style rules: 10–15 digits, optional + prefix, country code 1–9.
 */

/** Min/max digit count for a valid phone (E.164 allows up to 15). */
const MIN_DIGITS = 10;
const MAX_DIGITS = 15;

/** Characters allowed in raw input (we normalize before validating). */
const ALLOWED_CHARS = /^[\d+\s\-().]*$/;

/** Shape emitted by `antd-phone-input` before normalization. */
export type AntdPhoneInputValue = {
  countryCode?: string | number;
  areaCode?: string | number;
  phoneNumber?: string | number;
  valid?: () => boolean;
};

/**
 * Normalize user input to digits only, with optional leading + for country code.
 * Strips spaces, dashes, parentheses, dots. Preserves a single leading +.
 */
export function normalizePhoneForValidation(value: string): string {
  if (!value || typeof value !== 'string') return '';
  const trimmed = value.trim();
  const hasPlus = trimmed.startsWith('+');
  const digitsOnly = trimmed.replace(/\D/g, '');
  return hasPlus ? `+${digitsOnly}` : digitsOnly;
}

/**
 * Normalize to E.164-like format for storage/display: +<digits> or just <digits>.
 * If user typed a leading +, keep it; otherwise return digits only.
 */
export function formatPhoneE164(value: string): string {
  const normalized = normalizePhoneForValidation(value);
  if (!normalized) return '';
  if (normalized.startsWith('+')) return normalized;
  return normalized;
}

/**
 * Coerce antd-phone-input object or string into an E.164 string for API payloads.
 * Use this whenever reading a phone field that may still hold the library's value object.
 */
export function toE164Phone(value: unknown): string | undefined {
  if (value == null || value === '') {
    return undefined;
  }
  if (typeof value === 'string') {
    const normalized = normalizePhoneForValidation(value);
    if (!normalized) return undefined;
    return normalized.startsWith('+') ? normalized : `+${normalized.replace(/\D/g, '')}`;
  }
  if (typeof value === 'object') {
    const v = value as AntdPhoneInputValue;
    const national = `${v.areaCode ?? ''}${v.phoneNumber ?? ''}`;
    if (v.countryCode == null || !national) {
      return undefined;
    }
    return `+${v.countryCode}${national}`;
  }
  return undefined;
}

/**
 * Light display formatting for E.164 numbers (groups of 3 from the end).
 * Does not change the stored/API value — use {@link toE164Phone} for that.
 */
export function formatPhoneForDisplay(value: string): string {
  const e164 = toE164Phone(value) ?? value.trim();
  if (!e164.startsWith('+')) {
    return e164;
  }
  const digits = e164.slice(1).replace(/\D/g, '');
  if (!digits) {
    return e164;
  }
  const groups: string[] = [];
  let rest = digits;
  while (rest.length > 3) {
    groups.unshift(rest.slice(-3));
    rest = rest.slice(0, -3);
  }
  if (rest) {
    groups.unshift(rest);
  }
  return `+${groups.join(' ')}`;
}

export interface PhoneValidationResult {
  valid: boolean;
  message?: string;
  normalized?: string;
}

/**
 * Validate a phone number string (after normalization).
 * - Empty is valid (optional field).
 * - Allows + prefix; digits only; 10–15 digits; first digit after + must be 1–9.
 */
export function validatePhoneNumber(value: string): PhoneValidationResult {
  const trimmed = (value ?? '').trim();
  if (!trimmed) return { valid: true };

  if (!ALLOWED_CHARS.test(trimmed)) {
    return {
      valid: false,
      message: 'Phone number can only contain digits, +, spaces, dashes, parentheses or dots.'
    };
  }

  const normalized = normalizePhoneForValidation(trimmed);
  const digitsOnly = normalized.replace(/\D/g, '');
  const digitCount = digitsOnly.length;

  if (digitCount < MIN_DIGITS) {
    return {
      valid: false,
      message: `Phone number must have at least ${MIN_DIGITS} digits.`,
      normalized: formatPhoneE164(trimmed)
    };
  }
  if (digitCount > MAX_DIGITS) {
    return {
      valid: false,
      message: `Phone number must have at most ${MAX_DIGITS} digits.`,
      normalized: formatPhoneE164(trimmed)
    };
  }

  const firstDigit = digitsOnly[0];
  if (firstDigit === '0') {
    return {
      valid: false,
      message: 'Country code cannot start with 0. Use + and the country code (e.g. +1 for US).',
      normalized: formatPhoneE164(trimmed)
    };
  }

  return {
    valid: true,
    normalized: normalized.startsWith('+') ? normalized : `+${digitsOnly}`
  };
}

/**
 * Form-friendly validator: returns error message string or undefined if valid.
 */
export function getPhoneValidationError(value: string): string | undefined {
  const result = validatePhoneNumber(value ?? '');
  return result.valid ? undefined : result.message;
}

/** Strict E.164: "+", a country code 1–9, then 7–14 more digits (8–15 digits in total). */
const E164_RE = /^\+[1-9]\d{7,14}$/;

/**
 * True when the value is a complete E.164 number, ignoring spaces, dashes, dots and parentheses.
 */
export function isE164Phone(value: string | null | undefined): boolean {
  if (!value) return false;
  return E164_RE.test(value.trim().replace(/[\s().-]/g, ''));
}
